import { ShieldCheck, FileSearch, Film, MonitorPlay, CircleSlash, EyeOff, UserCheck, Megaphone, CheckCircle2, AlertTriangle } from "lucide-react";
import { SectionHead, Reveal } from "@/components/public/home/ui";
import { Window } from "@/components/public/home/mocks";

const CHECKS = [
  ["Candidate consent", "Pass", "Recorded before the interview starts"],
  ["Identity photo", "Pass", "Face-aligned photo captured before the video round"],
  ["Tab switches (video)", "Pass", "0 switches during interview"],
  ["Interruptions (video)", "Pass", "Completed in one sitting"],
  ["Tab switches (coding)", "Review", "3 switches during coding round"],
  ["Paste events (coding)", "Pass", "No text pasted from outside the editor"],
  ["AI disclosure (phone)", "Pass", "Disclosure read at the start of every call"],
];

const TRUST = [
  { Icon: Film, t: "Recorded video for review and audit", d: "With the candidate's consent, video interviews are securely recorded so authorised team members can review responses, verify identity and keep an audit trail. Access follows your role permissions." },
  { Icon: MonitorPlay, t: "Proctored coding sessions", d: "Coding rounds run under remote proctoring — webcam and screen-share recording plus tab-switch, paste and keystroke monitoring. Candidates are informed and asked for permission before they begin." },
  { Icon: FileSearch, t: "Evidence behind every score", d: "Full transcripts, final code and run output, plus the AI's reasoning, highlights and concerns — on screen and in the PDF." },
  { Icon: CircleSlash, t: "Unfinished rounds are never scored", d: "Hang-ups, half-finished videos and abandoned coding rounds are marked Incomplete instead of getting a misleading score." },
  { Icon: EyeOff, t: "Unbiased review mode", d: "Hide candidate names and contact details while reviewing, so the evidence speaks first." },
  { Icon: Megaphone, t: "Transparent to candidates", d: "Consent is recorded and every AI phone call opens with a clear AI disclosure." },
  { Icon: UserCheck, t: "AI recommends, people decide", d: "Scores and verdicts guide your shortlist — recruiters and hiring managers make the final call." },
];

function IntegrityMock() {
  return (
    <Window dark={false} title="recruitsift.com · Integrity checks · Alex Rivera">
      <div className="p-4 sm:p-5">
        <div className="divide-y divide-slate-100">
          {CHECKS.map(([c, s, d], i) => (
            <div key={c} className="flex items-start gap-3 py-2.5 rs-fade-up" style={{ animationDelay: `${i * 90}ms` }} data-testid={`integrity-row-${i}`}>
              {s === "Pass" ? <CheckCircle2 className="w-4 h-4 mt-0.5 text-emerald-500 shrink-0" /> : <AlertTriangle className="w-4 h-4 mt-0.5 text-amber-500 shrink-0" />}
              <div className="flex-1 min-w-0"><div className="text-sm font-medium text-slate-900">{c}</div><div className="text-xs text-slate-500">{d}</div></div>
              <span className={`text-[10px] font-mono px-2 py-0.5 rounded-full border ${s === "Pass" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>{s.toUpperCase()}</span>
            </div>
          ))}
        </div>
        <div className="mt-3 rounded-xl bg-slate-50 p-3 text-[11px] font-mono text-slate-500 leading-relaxed">
          Session timeline · Invite sent 09:02 · Video 10:14 → 10:41 (27m) · Coding 10:45 → 11:30 (45m)
        </div>
      </div>
    </Window>
  );
}

export function TrustSection() {
  return (
    <section id="trust" className="bg-white scroll-mt-16" data-testid="trust-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-20 sm:py-28">
        <SectionHead eyebrow={<><ShieldCheck className="w-3.5 h-3.5" /> Integrity & trust</>} title="Interviews you can verify. Scores you can defend."
          sub="Every RecruitSift interview carries integrity checks and the evidence behind its score — so you, your hiring managers and your clients can trust the shortlist." />
        <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <Reveal className="lg:col-span-5 min-w-0">
            <div className="rounded-3xl bg-gradient-to-br from-emerald-50 via-white to-indigo-50 border border-slate-200 p-4 sm:p-6"><IntegrityMock /></div>
            <p className="mt-4 text-xs text-slate-500">Video rounds also record time spent away; coding rounds record keystrokes, pasted code and run history. Checks marked Review are flagged for a recruiter — never hidden.</p>
          </Reveal>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
            {TRUST.map(({ Icon, t, d }, i) => (
              <Reveal key={t} delay={i * 0.05} className={i === TRUST.length - 1 ? "sm:col-span-2" : ""}>
                <div className="h-full rounded-2xl border border-slate-200 p-6 hover:border-indigo-300 hover:shadow-lg transition-[border-color,box-shadow] duration-300" data-testid={`trust-card-${i}`}>
                  <div className="w-10 h-10 rounded-xl bg-indigo-50 border border-indigo-100 grid place-items-center"><Icon className="w-5 h-5 text-indigo-600" /></div>
                  <div className="mt-4 font-display text-base font-semibold text-slate-900">{t}</div>
                  <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
